import type { CarbonTargetDto } from '@siliconx/shared';
import { reductionTargetProgress } from '@siliconx/shared';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { carbonOrgTargetLabel } from './carbonCopy';

/**
 * SIL-474 ("Demo: b-lite reduction-% target type + FY30 card") — the FY
 * target card for an org's `carbon_target` headline row.
 *
 * SELF-CONTAINED + VARIANT SLOT: an org's headline target is EITHER
 * absolute_budget OR reduction_percent (never both — 718's org-headline
 * uniqueness index, unchanged by migration 722). This one exported
 * component is the mount point for BOTH: it reads `target.targetType` and
 * renders the matching variant internally, so a caller never has to branch
 * on target shape itself — it just passes whatever `GET /sustainability/
 * targets`'s `org` field currently holds. `null` (no org target set) and a
 * malformed row (a `reduction_percent` row missing one of its four required
 * fields, which the DB CHECK `carbon_target_type_fields_coherence` should
 * never actually allow) both render nothing rather than guess.
 *
 * NOT the SIL-426 locked copy inventory (§7) — this is a new, ungated demo
 * surface with no locked customer-facing sentence of its own. Every string
 * below is written to that inventory's OWN conventions instead (no
 * "offset"/"credit"/"carbon neutral"/"capital goods", no bare "per"
 * conformance claim), mirroring how `carbonCopy.ts`'s SIL-443 sensitivity
 * strings were added post-vet.
 *
 * NEVER MOUNTED HERE: this file only exports the component. The SIL-472
 * Environmental Overview tab (a parallel branch) owns where it lands on
 * that page — see that ticket for the mount. This ticket mounts it wherever
 * the pre-existing absolute-budget target already renders on the
 * sustainability trajectory route, since that is the one surface today that
 * already reads the org headline target (see
 * apps/customer/src/routes/financials/sustainability.tsx).
 */

export interface FyReductionTargetCardProps {
  /** The org headline `carbon_target` row (`GET /sustainability/targets`'s
   * `org` field), or `null` when the org has none set. */
  target: CarbonTargetDto | null;
  /**
   * Current annual emissions, tCO2e/yr — from the EXISTING org emissions
   * aggregate (e.g. the sustainability-trajectory route's
   * `fleetAnnualRateKgco2ePerYr`, converted from kgCO2e to tCO2e by the
   * caller: `/ 1000`). `null` while that aggregate has not loaded yet, or
   * is unavailable — the reduction-percent variant renders its base-year
   * figures either way and simply omits the live progress bar without one.
   */
  currentAnnualTco2e: number | null;
}

/** Clamped to [0, 1] for the progress-bar WIDTH only — the underlying
 * `reductionTargetProgress` ratio itself is never clamped (see its own
 * docstring), so a caller reading the raw number can still tell "on track"
 * from "already past" from "moving the wrong direction". */
function clampProgress(ratio: number): number {
  return Math.max(0, Math.min(1, ratio));
}

function formatTco2e(n: number): string {
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 1 })} tCO2e`;
}

export function FyReductionTargetCard({ target, currentAnnualTco2e }: FyReductionTargetCardProps) {
  if (!target) return null;

  if (target.targetType === 'absolute_budget') {
    return <AbsoluteBudgetVariant target={target} />;
  }

  return <ReductionPercentVariant target={target} currentAnnualTco2e={currentAnnualTco2e} />;
}

/**
 * Absolute-budget variant — the org's headline target is a flat annual
 * budget, not a reduction percent. This variant renders the SAME §12 chart
 * label already used elsewhere (`carbonOrgTargetLabel`) so a reader sees
 * one consistent sentence for the absolute-budget shape everywhere it
 * appears, rather than a second, differently-worded card.
 */
function AbsoluteBudgetVariant({ target }: { target: CarbonTargetDto }) {
  if (target.tco2ePerYr == null) return null;
  return (
    <Card as="section" padding="md" className="space-y-1">
      <SectionLabel>Organization target</SectionLabel>
      <p className="text-sm font-medium text-zinc-800">{carbonOrgTargetLabel(target.tco2ePerYr)}</p>
    </Card>
  );
}

function ReductionPercentVariant({
  target,
  currentAnnualTco2e,
}: {
  target: CarbonTargetDto;
  currentAnnualTco2e: number | null;
}) {
  const { baseYear, baseYearTco2e, targetYear, reductionPct } = target;
  // Coherence CHECK (722) guarantees these are all set together for a
  // reduction_percent row — this guard exists only for a malformed row the
  // DB should never actually produce, per the header above.
  if (baseYear == null || baseYearTco2e == null || targetYear == null || reductionPct == null) {
    return null;
  }

  const progress =
    currentAnnualTco2e == null ? null : reductionTargetProgress(baseYearTco2e, currentAnnualTco2e, reductionPct);
  const progressPct = progress == null ? null : Math.round(clampProgress(progress) * 1000) / 10;

  return (
    <Card as="section" padding="md" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionLabel>{`FY${targetYear} target`}</SectionLabel>
        <span className="text-xs text-zinc-500">
          {reductionPct.toLocaleString('en-US', { maximumFractionDigits: 2 })}% below {baseYear}
        </span>
      </div>

      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-xs text-zinc-500">Base-year emissions ({baseYear})</span>
        <span className="text-sm font-medium text-zinc-800">{formatTco2e(baseYearTco2e)}</span>
        <StatusPill tone="neutral" title="Entered by the organization, not computed by the platform.">
          Customer-provided
        </StatusPill>
      </div>

      {currentAnnualTco2e != null && (
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-xs text-zinc-500">Current annual</span>
          <span className="text-sm font-medium text-zinc-800">{formatTco2e(currentAnnualTco2e)}</span>
        </div>
      )}

      {progressPct != null ? (
        <div className="space-y-1">
          <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-100">
            <div
              className="h-full rounded-full bg-emerald-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-xs text-zinc-500">
            {progressPct}% of the FY{targetYear} reduction target reached, based on current annual emissions
            vs the {baseYear} base year.
          </p>
        </div>
      ) : (
        <p className="text-xs text-zinc-500">
          Waiting on a current-annual emissions figure to compute progress against the {baseYear} base year.
        </p>
      )}
    </Card>
  );
}
